import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import stock from "../../../stock.json";
import { Modal } from "bootstrap";
import UserServices from "../../../services/UserServices";
import { ThemeToggle } from "../../../components/ThemeToggle";
import { useTheme } from "../../../context/ThemeContext";

const OTW_HEADER_SENTIMENT_EVENT = "otw-header-sentiment";

// Content pages whose header shows no symbol search (title + theme toggle only).
const NO_SYMBOL_SEARCH_PATHS = [
  '/blog', '/dailyanalysis', '/about', '/contact', '/privacy-policy',
  '/term-and-services', '/cancelpolicy', '/pricing', '/profile', '/trading-video', '/hot-stock', '/moment-indicator',
  '/sr-stock',
];

// Sidebar profile name: the stored user (plain name or JSON with a name), title-cased.
const displayUserName = (raw) => {
  let name = raw || '';
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') name = parsed.name || parsed.fullName || parsed.userName || parsed.email || '';
    else if (typeof parsed === 'string') name = parsed;
  } catch (_) { /* plain string */ }
  name = String(name).trim();
  return name ? name.replace(/\s+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : 'My Profile';
};

// Sidebar section: a parent tab whose submenu opens underneath it on click. The section that
// contains the current page (or marked defaultOpen) starts open. Children are the existing menu <li> items, unchanged.
function SidebarGroup({ title, icon, children, defaultOpen = false }) {
  const location = useLocation();
  const hasActivePage = React.Children.toArray(children).some((child) => {
    const link = React.Children.toArray(child?.props?.children).find((c) => c?.props?.to);
    return link && link.props.to === location.pathname;
  });
  const [open, setOpen] = React.useState(defaultOpen || hasActivePage);
  React.useEffect(() => {
    if (hasActivePage) setOpen(true);
  }, [hasActivePage]);
  return (
    <li className="sidenav-group">
      <button
        type="button"
        className="sidenav-group-toggle"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <i className={`fa ${icon} sidenav-group-icon`} aria-hidden="true" />
        <span>{title}</span>
        <i className={`fa fa-chevron-${open ? "up" : "down"} sidenav-group-caret`} aria-hidden="true" />
      </button>
      {open && <ul className="list-unstyled sidenav-submenu">{children}</ul>}
    </li>
  );
}

function Header({ onSearchChange, trading, tradingTwo }) {
  const [headerTrading, setHeaderTrading] = React.useState(trading);
  const [headerTradingTwo, setHeaderTradingTwo] = React.useState(tradingTwo);

  React.useEffect(() => {
    setHeaderTrading(trading);
  }, [trading]);

  React.useEffect(() => {
    setHeaderTradingTwo(tradingTwo);
  }, [tradingTwo]);

  React.useEffect(() => {
    const onSentiment = (e) => {
      const d = e?.detail;
      if (d && d.trading !== undefined) setHeaderTrading(d.trading);
      if (d && d.tradingTwo !== undefined) setHeaderTradingTwo(d.tradingTwo);
    };
    window.addEventListener(OTW_HEADER_SENTIMENT_EVENT, onSentiment);
    return () => window.removeEventListener(OTW_HEADER_SENTIMENT_EVENT, onSentiment);
  }, []);

  const location = useLocation();
  const [stockT, setStock] = React.useState(
    localStorage.getItem("stock") || "Nifty"
  );
  const [lot, setLot] = React.useState(localStorage.getItem("expiry"));
  const [expiry, setExpiry] = React.useState("30May2024");
  const [showSearch, setShowSearch] = React.useState(false);
  const [tempToken, setTempToken] = React.useState("");
  const [showOtpInput, setShowOtpInput] = React.useState(false);
  const [isLoggedIn, setIsLoggedIn] = React.useState(false); // State for login status
  const [otpValue, setOtpValue] = React.useState();
  const [email, setEmail] = React.useState("");
  const [password, setPaasword] = React.useState("");
  const [otpError, setOtpError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = React.useState("");

  // The search popup starts empty every time: clear the query whenever it closes
  // (close button, backdrop click, Esc).
  React.useEffect(() => {
    const modal = document.getElementById('searchNiftyModel');
    if (!modal) return undefined;
    const clearSearch = () => setSearchQuery("");
    modal.addEventListener('hidden.bs.modal', clearSearch);
    return () => modal.removeEventListener('hidden.bs.modal', clearSearch);
  }, []);

  const { theme, setTheme } = useTheme();
  // Sidebar: close after choosing a page, after any navigation, and on a click outside it.
  React.useEffect(() => {
    const nav = document.getElementById("mySidenav");
    if (!nav) return undefined;
    const close = () => { nav.style.transform = "translateX(-375px)"; };
    const isOpen = () => nav.style.transform === "translateX(0%)";
    const onClick = (e) => {
      if (!isOpen()) return;
      if (nav.contains(e.target)) {
        // a menu link (not a section header) was chosen
        if (e.target.closest("a")) close();
        return;
      }
      if (e.target.closest(".toggle-btn-outer")) return; // the open button itself
      close();
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);
  React.useEffect(() => {
    const nav = document.getElementById("mySidenav");
    if (nav) nav.style.transform = "translateX(-375px)";
  }, [location.pathname]);
  const headerRef = React.useRef(null);
  const [headerHeight, setHeaderHeight] = React.useState(0);
  React.useEffect(() => {
    const el = headerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    // Next frame + only on a real change, so the observer never loops.
    let frame = 0;
    const ro = new ResizeObserver(() => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        setHeaderHeight((h) => (h === el.offsetHeight ? h : el.offsetHeight));
      });
    });
    ro.observe(el);
    setHeaderHeight((h) => (h === el.offsetHeight ? h : el.offsetHeight));
    return () => {
      if (frame) cancelAnimationFrame(frame);
      ro.disconnect();
    };
  }, [location.pathname]); // the header element exists only on pages that show it
  const [lightMode, setLightMode] = React.useState(
    JSON.parse(localStorage.getItem("lightMode"))
  );
  const [itmOtmHighlight, setItmOtmHighlight] = React.useState(
    JSON.parse(localStorage.getItem("itmOtmHighlight"))
  );
  const [keepScreenAwake, setKeepScreenAwake] = React.useState(
    JSON.parse(localStorage.getItem("keepScreenAwake"))
  );
  const [showFuturePrice, setShowFuturePrice] = React.useState(
    JSON.parse(localStorage.getItem("showFuturePrice"))
  );
  const [highThirdHighest, setHighThirdHighest] = React.useState(
    JSON.parse(localStorage.getItem("highThirdHighest"))
  );
  const [showNumbers, setShowNumbers] = React.useState(
    JSON.parse(localStorage.getItem("showNumbers"))
  );
  const [reverseTable, setReverseTable] = React.useState(
    JSON.parse(localStorage.getItem("reverseTable"))
  );
  const [preferPercentage, setPreferPercentage] = React.useState(
    JSON.parse(localStorage.getItem("preferPercentage"))
  );
  const [showQt, setShowQt] = React.useState(
    JSON.parse(localStorage.getItem("showQt"))
  );
  const [clearDataCache, setClearDataCache] = React.useState(
    JSON.parse(localStorage.getItem("clearDataCache"))
  );
  const [showColoum, setshowColoum] = React.useState(
    JSON.parse(localStorage.getItem("showColoum"))
  );
  const [showDataInLots, setShowDataInLots] = React.useState(
    JSON.parse(localStorage.getItem("showDataInLots"))
  );



  // Function to handle changes in the search input
  // const handleSearchChange = (e) => {
  //   setSearchQuery(e.target.value);
  // };
  const handleSearchChange = (e) => {
    const upperCaseValue = e.target.value.toUpperCase();
    setSearchQuery(upperCaseValue); // assuming setSearchQuery is your state setter
  };


  React.useEffect(() => {
    const userLoggedIn = !!localStorage.getItem("user");
    setIsLoggedIn(userLoggedIn);
  }, []);
  const handleSetLocalStorage = (name, expiry, item) => {
    setSearchQuery(""); // the popup is hidden by hand below, so hidden.bs.modal does not fire
    const modal = document.getElementById('searchNiftyModel');
    modal.classList.remove('show', 'modal-backdrop');
    modal.setAttribute('aria-hidden', 'true');
    modal.style.display = 'none';
    const modelee = document.getElementsByClassName('modal-backdrop fade show')

    console.log(modelee)
    modelee[0].setAttribute('style', 'display: none !important');
    modelee[1].setAttribute('style', 'display: none !important');
    modelee[2].setAttribute('style', 'display: none !important');
    console.log(modelee)


    setShowSearch(!showSearch);
    localStorage.setItem("stock", name);
    localStorage.setItem("expiry", expiry);
    setStock(name);
    setLot(expiry);
    const object = {
      name: name,
      expiry: expiry,
      symbolToken: item.symbolToken,
      tradingSymbol: item.tradingSymbol,
    };
    onSearchChange(name);
    // const modal = document.getElementById('searchNiftyModel');
    // modal.classList.remove('show', 'modal-backdrop');
    // modal.setAttribute('aria-hidden', 'true');
    // modal.style.display = 'none';


  };
  const [favoriteList, setFavoriteList] = useState(() => {
    const stored = localStorage.getItem("favoriteStocks");
    return stored ? JSON.parse(stored) : [];
  });

  const toggleFavorite = (name) => {
    const updatedFavorites = favoriteList.includes(name)
      ? favoriteList.filter((fav) => fav !== name)
      : [...favoriteList, name];

    setFavoriteList(updatedFavorites);
    localStorage.setItem("favoriteStocks", JSON.stringify(updatedFavorites));
  };
  const [signupDetails, setSignupDetails] = React.useState({
    name: "",
    email: "",
    mobileNo: "",
    password: "",
  });

  const handleSignupInputChange = (e) => {
    const { name, value } = e.target;
    setSignupDetails((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };
  const handleSignup = async (e) => {
    e.preventDefault();

    try {
      const data = await UserServices.userRegister({
        name: signupDetails.name,
        email: signupDetails.email,
        mobileNo: signupDetails.mobileNo,
        password: signupDetails.password,
      });
      console.log(data);

      if (data?.status === true) {
        // Store the user token temporarily (until OTP is verified)
        const verifyToken = data?.data?.verifyToken;
        localStorage.setItem("tempUserToken", verifyToken);
        setTempToken(verifyToken);
        setShowOtpInput(true);
      } else {
        alert("Signup failed. Please check your input.");
      }
    } catch (error) {
      console.error("Signup failed:", error); // Log error for debugging
      alert("Signup failed. Please try again later.");
    }
  };

  const handleToggle = (settingName, setter) => {
    setter((prevState) => {
      const newValue = !prevState;
      localStorage.setItem(settingName, JSON.stringify(newValue)); // Save to localStorage
      window.dispatchEvent(new CustomEvent('otw:setting', { detail: { name: settingName, value: newValue } }));
      return newValue;
    });
  };

  const handleProtectedNavigation = (path) => {
    if (isLoggedIn) {
      console.log(path);
      navigate(path);
    } else {
      // Show login modal
      console.log("not login");
      const loginModal = new Modal(document.getElementById("loginModel"));
      loginModal.show();
    }
  };

  const filteredStock = stock.filter((item) =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleLogin = async (e) => {
    e.preventDefault();
    console.log(email, password);
    try {
      const data = await UserServices.userLogin({
        mobileOrEmail: email,
        password: password,
      });
      console.log("data>>>", data);
      if (data.status === true) {
        // Store the user token temporarily (until OTP is verified)
        const verifyToken = data.data?.verifyToken;
        localStorage.setItem("tempUserToken", verifyToken);
        setTempToken(verifyToken);
        setShowOtpInput(true);
      } else {
        alert("Login failed. Please check your credentials.");
      }
    } catch (error) {
      alert("Login failed.");
    }
  };

  const handleOTPVerification = async (otp) => {
    try {
      const data = await UserServices.verifyOtp({
        userToken: tempToken,
        otp: String(otpValue),
        page: "login",
      });
      console.log(data);

      if (data.status === true) {
        // Store the user data
        localStorage.setItem("user", data.data?.user?.name);
        localStorage.removeItem("tempUserToken");
        localStorage.setItem("token", data.data?.accessToken || data.data?.token);
        setIsLoggedIn(true);
        window.location.reload();
        // Hide the OTP modal
        const loginModal = new Modal(document.getElementById("loginModel"));
        loginModal.hide();
      } else {
        alert("OTP verification failed.");
      }
    } catch (error) {
      alert("OTP verification failed.");
    }
  };

  const handleSignUpOTPVerification = async (otp) => {
    try {
      const data = await UserServices.verifyOtp({
        userToken: tempToken,
        otp: String(otpValue),
        page: "register",
      });
      console.log(data);

      if (data.status === true) {
        // Store the user data
        localStorage.setItem("user", data.data?.user?.name);
        localStorage.removeItem("tempUserToken");
        localStorage.setItem("token", data.data?.accessToken || data.data?.token);
        setIsLoggedIn(true);
        window.location.reload();
        // Hide the OTP modal
        const loginModal = new Modal(document.getElementById("loginModel"));
        loginModal.hide();
      } else {
        alert("OTP verification failed.");
      }
    } catch (error) {
      alert("OTP verification failed.");
    }
  };

  const handleOpenModal = () => {
    setShowSearch(true);  // Open modal
  };

  // Function to close modal
  const handleCloseModal = () => {
    setShowSearch(false); // Close modal
  };

  const pathTitleMap = {
    "/about": "About Us",
    "/contact": "Contact Us",
    "/profile": "Profile",
    "/privacy-policy": "Privacy Policy",
    "/term-and-services": "Terms of Services",
    "/cancelpolicy": "Cancelation Refund Policy",
    "/sr-stock": "Stocks at Support & Resistance",
    "/sr-stock": "Stocks at Support & Resistance",
    "/trading-video": "Technical Analysis Course",
    "/strong-sr-stock": "Triple Support & Resistance",
    "/scanner": "Scanner View Trading",
    "/blog": "Blog",
    "/dailyanalysis": "Daily Analysis",
    "/daily-analysis-chart": "Daily Analysis Chart",
    "/tainding-stock": "Tainding Stock",
    "/hot-option": "Hot Option",
    "/hot-chart": "Hot Chart",
    "/moment-indicator": "Moment Indicator",
    "/hot-stock": "Hot Stocks",
    // Add other paths and titles here
  };
  const pageTitle = pathTitleMap[location.pathname] || "Page Title";
  // No top header on Home, Power Stocks and Pricing (those pages show their own sidebar toggle).
  // Option Chain / Backtester: header fixed at the top while the table scrolls.
  const fixedHeader = location.pathname === "/optionchain" || location.pathname === "/backtester";
  const showHeader = location.pathname === "/" || location.pathname === "/power-stocks" || location.pathname === "/pricing";
  const showStock =
    location.pathname === "/optionchain" || location.pathname === "/backtester" || location.pathname === "/vegaTable" || location.pathname === "/straddleChart" || location.pathname === "/optionSnapshot";
  console.log("lot", lot)
  console.log("localStorage.getItem", localStorage.getItem("expiry"))
  return (
    <>
      {!showHeader && (
        <header
          ref={headerRef}
          className={`sticky top-0 z-40 border-b border-slate-200/80 bg-white/90 shadow-sm backdrop-blur dark:border-slate-800 dark:bg-slate-900/90${fixedHeader ? " otw-fixed-header" : ""}`}
        >
          <div className="container-fluid px-2 sm:px-4">
            <div className="header-inner flex flex-wrap items-center justify-between gap-3 py-2 md:flex-nowrap lg:gap-4">
              <div><div className="toggle-btn-outer" style={{ float: "left" }}>
                <button>
                  <i className="fa fa-bars" />
                </button>

              </div>
                <img src="img/header-logo.svg" alt="footer-logo" />
              </div>
              {showStock ? (
                <>
                  <div className="page-title">
                    <h3>
                      {" "}
                      <strong>  {stockT} </strong>|

                      <button
                        type="button"
                        className="btn btn-primary mb-2"
                        style={{
                          backgroundColor: '#f77c22',
                          border: '2px solid #f77c22',
                          borderRadius: '10px',
                          padding: '5px 15px',
                          fontWeight: 'bold',
                          fontSize: '14px',
                          marginBottom: "10px"
                        }}
                      >
                        LOT SIZE {lot}
                      </button>

                    </h3>
                  </div>
                  <div className="">
                    <h3>
                      {/* <strong>Daily: {trading} </strong> */}
                      <strong >
                        DAILY:{" "}
                        <strong
                          className={
                            headerTrading !== "Bearish" && headerTrading !== "Bullish"
                              ? "text-slate-900 dark:text-slate-200"
                              : ""
                          }
                          style={{
                            color:
                              headerTrading === "Bearish"
                                ? "red"
                                : headerTrading === "Bullish"
                                  ? "green"
                                  : undefined,
                          }}
                        >
                          {headerTrading}
                        </strong>
                      </strong>

                    </h3>
                  </div>
                  <div className="">
                    <h3 >
                      <strong>
                        INTRADAY:{" "}
                        <strong
                          className={
                            headerTradingTwo !== "Bearish" && headerTradingTwo !== "Bullish"
                              ? "text-slate-900 dark:text-slate-200"
                              : ""
                          }
                          style={{
                            color:
                              headerTradingTwo === "Bearish"
                                ? "red"
                                : headerTradingTwo === "Bullish"
                                  ? "green"
                                  : undefined,
                          }}
                        >
                          {headerTradingTwo}
                        </strong>
                      </strong>
                    </h3>
                  </div>

                  <div className="search-btn-outer">
                    <button
                      data-bs-toggle="modal"
                      onClick={handleOpenModal}
                      data-bs-target="#searchNiftyModel"

                    >
                      <i className="fa fa-search me-2" />
                      {stockT}
                    </button>
                  </div>
                  <ThemeToggle className="shrink-0" />
                </>
              ) : (
                <>
                  <div className="page-title">
                    <h3>
                      {" "}
                      <strong>{pageTitle} </strong>{" "}
                    </h3>
                  </div>
                  {/* Symbol search only on pages that work with a symbol (not on content pages) */}
                  {!NO_SYMBOL_SEARCH_PATHS.some((p) => location.pathname === p || location.pathname.startsWith(`${p}/`)) && (
                    <div className="search-btn-outer">
                      <button
                        data-bs-toggle="modal"
                        onClick={handleOpenModal}
                      >
                        <i className="fa fa-search me-2" />
                        {stockT}
                      </button>
                    </div>
                  )}
                  <ThemeToggle className="shrink-0" />
                </>
              )}
            </div>
          </div>
        </header>
      )}
      {/* Keeps page content below the fixed header (Option Chain / Backtester) */}
      {!showHeader && fixedHeader && <div aria-hidden="true" style={{ height: headerHeight }} />}
      <div className="sidenav" id="mySidenav">
        <div className="toggle-btn-outer navClose">
          <button className="active">
            <i className="fa fa-close" />
          </button>
        </div>
        <div className="sidenav-logo">
          <Link to="/">
            <img src="img/header-logo.svg" alt="logo" />
          </Link>
        </div>
        <div className="sidenav-menus">
          <ul className="list-unstyled">
            <SidebarGroup title="Option Chain" icon="fa-table" defaultOpen>
                <li>
                  <Link
                    to="#"
                    onClick={(e) => {
                      e.preventDefault(); // Prevent default anchor behavior
                      handleProtectedNavigation("/optionchain");
                    }}
                    className={isLoggedIn ? "" : "active"}
                  >
                    <span>
                      <svg
                        width={22}
                        height={22}
                        viewBox="0 0 22 22"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M13.9306 3.36401C15.4644 1.8473 17.827 1.72434 19.2075 3.08938C20.588 4.45443 20.4636 6.79053 18.9297 8.30724L16.7079 10.5043M9.2092 12.9759C7.82873 11.6109 7.95308 9.27473 9.48695 7.75802L11.4583 5.80866"
                          stroke="#637B97"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                        <path
                          d="M12.7907 9.36426C14.1712 10.7293 14.0468 13.0654 12.513 14.5821L10.2912 16.7791L8.06933 18.9762C6.53547 20.4929 4.17293 20.6158 2.79245 19.2508C1.41198 17.8857 1.53634 15.5496 3.07019 14.0329L5.29205 11.8358"
                          stroke="#637B97"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                      </svg>
                    </span>
                    Option Trading Wala
                  </Link>
                </li>
                <li>
                  <Link
                    to="#"
                    onClick={(e) => {
                      e.preventDefault(); // Prevent default anchor behavior
                      handleProtectedNavigation("/backtester");
                    }}
                    className={isLoggedIn ? "" : "active"}
                  >
                    <span>
                      <svg
                        width={24}
                        height={24}
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M6.49976 18.8754V17.7919C6.49976 14.7999 8.96219 12.3744 11.9998 12.3744C15.0373 12.3744 17.4998 14.7999 17.4998 17.7919V18.8754"
                          stroke="#637B97"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                        <path
                          d="M11.9992 12.3752C13.8218 12.3752 15.2992 10.9199 15.2992 9.12468C15.2992 7.32948 13.8218 5.87418 11.9992 5.87418C10.1766 5.87418 8.69922 7.32948 8.69922 9.12468C8.69922 10.9199 10.1766 12.3752 11.9992 12.3752Z"
                          stroke="#637B97"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M12 23.2098C18.0751 23.2098 23 18.3588 23 12.3748C23 6.39079 18.0751 1.53979 12 1.53979C5.92487 1.53979 1 6.39079 1 12.3748C1 18.3588 5.92487 23.2098 12 23.2098Z"
                          stroke="#637B97"
                          strokeWidth="1.5"
                        />
                      </svg>
                    </span>
                    Back Tester
                  </Link>
                </li>
                {/* <li>
                  <Link to="/optionSnapshot">
                    <span>
                      <svg
                        width={24}
                        height={25}
                        viewBox="0 0 24 25"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M1 7.02481L11.6399 11.6005L12.3601 10.0325L1.7202 5.45682L1 7.02481ZM12.8867 23.3995V10.8165H11.1133V23.3995H12.8867ZM12.3601 11.6005L23 7.02481L22.2798 5.45682L11.6399 10.0325L12.3601 11.6005Z"
                          fill="#637B97"
                        />
                        <path
                          d="M1.35938 18.3784V6.68732C1.35938 6.41609 1.52446 6.1703 1.78062 6.06014L11.7112 1.7895C11.8946 1.71063 12.1039 1.71063 12.2874 1.7895L22.2179 6.06014C22.4741 6.1703 22.6392 6.41609 22.6392 6.68732V18.3784C22.6392 18.6496 22.4741 18.8954 22.2179 19.0056L12.2874 23.2762C12.1039 23.355 11.8946 23.355 11.7112 23.2762L1.78062 19.0056C1.52446 18.8954 1.35938 18.6496 1.35938 18.3784Z"
                          stroke="#637B97"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M6.67969 3.95428L16.8984 8.34881C17.1545 8.45897 17.3196 8.70476 17.3196 8.97599V13.1056"
                          stroke="#637B97"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                    Option Snapshot
                  </Link>
                </li>
                <li>
                  <Link to="/straddleChart">
                    <span>
                      <svg
                        width={24}
                        height={25}
                        viewBox="0 0 24 25"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M1 7.02481L11.6399 11.6005L12.3601 10.0325L1.7202 5.45682L1 7.02481ZM12.8867 23.3995V10.8165H11.1133V23.3995H12.8867ZM12.3601 11.6005L23 7.02481L22.2798 5.45682L11.6399 10.0325L12.3601 11.6005Z"
                          fill="#637B97"
                        />
                        <path
                          d="M1.35938 18.3784V6.68732C1.35938 6.41609 1.52446 6.1703 1.78062 6.06014L11.7112 1.7895C11.8946 1.71063 12.1039 1.71063 12.2874 1.7895L22.2179 6.06014C22.4741 6.1703 22.6392 6.41609 22.6392 6.68732V18.3784C22.6392 18.6496 22.4741 18.8954 22.2179 19.0056L12.2874 23.2762C12.1039 23.355 11.8946 23.355 11.7112 23.2762L1.78062 19.0056C1.52446 18.8954 1.35938 18.6496 1.35938 18.3784Z"
                          stroke="#637B97"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M6.67969 3.95428L16.8984 8.34881C17.1545 8.45897 17.3196 8.70476 17.3196 8.97599V13.1056"
                          stroke="#637B97"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                    Straddle Chart
                  </Link>
                </li> */}
            </SidebarGroup>
            <SidebarGroup title="Stock Screeners" icon="fa-filter">
                <li>
                  <Link to="/power-stocks">
                    <span><i className="fa fa-bolt sidenav-item-icon" aria-hidden="true" /></span>
                    Power Stocks
                  </Link>
                </li>
            
                <li>
                  <Link to="/sr-stock">
                    <span><i className="fa fa-th-large sidenav-item-icon" aria-hidden="true" /></span>
                    S/R Blocks
                  </Link>
                </li>
                {/* <li>
                  <Link to="/strong-sr-stock">
                    <span><i className="fa fa-align-justify sidenav-item-icon" aria-hidden="true" /></span>
                    Triple S/R
                  </Link>
                </li> */}
    {/* <li>
        <Link to="/selling-stock-index">
        <span><i className="fa fa-arrow-circle-down sidenav-item-icon" aria-hidden="true" /></span>
         Selling Stock / Index
          </Link>
        </li> */}
                {/* Bullish / Bearish with Trend */}
                {/* <li>
                  <Link to="/trend-with-stocks">
                    <span><i className="fa fa-line-chart sidenav-item-icon" aria-hidden="true" /></span>
                    Bullish / Bearish with Trend
                  </Link>
                </li> */}
                <li>
                  <Link to="/hot-stock">
                    <span><i className="fa fa-fire sidenav-item-icon" aria-hidden="true" /></span>
                    Hot Stock
                  </Link>
                </li>
                {/* <li>
                  <Link to="/hot-option">
                    <span><i className="fa fa-thermometer-full sidenav-item-icon" aria-hidden="true" /></span>
                    Hot Option
                  </Link>
                </li> */}
                {/* <li>
                  <Link to="/hot-chart">
                    <span><i className="fa fa-area-chart sidenav-item-icon" aria-hidden="true" /></span>
                    Hot Chart
                  </Link>
                </li> */}
                {/* <li>
                  <Link to="/tainding-stock">
                    <span><i className="fa fa-rocket sidenav-item-icon" aria-hidden="true" /></span>
                    Tranding Stock
                  </Link>
                </li> */}
                <li>
                  <Link to="/moment-indicator">
                    <span><i className="fa fa-tachometer sidenav-item-icon" aria-hidden="true" /></span>
                    Moment Indicator
                  </Link>
                </li>
                <li>
                  <Link to="/scanner">
                    <span><i className="fa fa-crosshairs sidenav-item-icon" aria-hidden="true" /></span>
                    Scanner
                  </Link>
                </li>
            </SidebarGroup>
            <SidebarGroup title="Learn &amp; Analysis" icon="fa-graduation-cap">
                <li>
                  <Link to="/dailyanalysis">
                    <span>
                      <svg
                        width={22}
                        height={22}
                        viewBox="0 0 22 22"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g clipPath="url(#clip0_472_80)">
                          <path
                            d="M12.7188 1.03125C11.17 1.03082 9.65234 1.46649 8.33962 2.2884C7.0269 3.11031 5.97214 4.28524 5.29608 5.67869C4.62001 7.07214 4.34997 8.62779 4.51686 10.1676C4.68375 11.7073 5.28083 13.169 6.23975 14.3853L1.03125 19.5938L2.40625 20.9688L7.61475 15.7603C8.64806 16.5748 9.86121 17.1304 11.1529 17.3806C12.4447 17.6309 13.7775 17.5686 15.0403 17.1989C16.303 16.8291 17.459 16.1628 18.4118 15.2553C19.3646 14.3479 20.0865 13.2257 20.5173 11.9825C20.9481 10.7393 21.0754 9.41104 20.8883 8.10864C20.7013 6.80624 20.2055 5.56746 19.4423 4.49569C18.6791 3.42391 17.6705 2.55023 16.5009 1.94756C15.3313 1.34489 14.0345 1.03072 12.7188 1.03125Z"
                            fill="#E5E6EB"
                          />
                          <path
                            d="M4.81241 15.8125L1.03125 19.5937L2.40628 20.9687L6.18745 17.1875L4.81241 15.8125Z"
                            fill="#637B97"
                          />
                          <path
                            d="M5.49991 16.5L1.71875 20.2811L2.40615 20.9685L6.18731 17.1874L5.49991 16.5Z"
                            fill="#637B97"
                          />
                          <path
                            d="M12.7188 16.1562C16.5157 16.1562 19.5938 13.0782 19.5938 9.28125C19.5938 5.48429 16.5157 2.40625 12.7188 2.40625C8.92179 2.40625 5.84375 5.48429 5.84375 9.28125C5.84375 13.0782 8.92179 16.1562 12.7188 16.1562Z"
                            fill="#637B97"
                          />
                          <path
                            d="M12.7188 15.8125C16.3259 15.8125 19.25 12.8884 19.25 9.28125C19.25 5.67414 16.3259 2.75 12.7188 2.75C9.11164 2.75 6.1875 5.67414 6.1875 9.28125C6.1875 12.8884 9.11164 15.8125 12.7188 15.8125Z"
                            fill="#CCEAFF"
                          />
                          <path
                            d="M17.5312 11.3438H8.25V12.0312H17.5312V11.3438Z"
                            fill="#637B97"
                          />
                          <path
                            d="M10.6562 8.9375H9.28125V11.6875H10.6562V8.9375Z"
                            fill="#637B97"
                          />
                          <path
                            d="M13.4062 6.875H12.0312V11.6875H13.4062V6.875Z"
                            fill="#637B97"
                          />
                          <path
                            d="M16.1562 6.1875H14.7812V11.6875H16.1562V6.1875Z"
                            fill="#637B97"
                          />
                          <path
                            d="M18.1974 3.11506C19.5872 4.68879 20.3248 6.73323 20.2598 8.83183C20.1947 10.9304 19.332 12.9253 17.8473 14.4099C16.3627 15.8946 14.3679 16.7573 12.2693 16.8223C10.1707 16.8874 8.12622 16.1498 6.55249 14.7599C7.29815 15.6042 8.20881 16.2869 9.22835 16.7658C10.2479 17.2448 11.3547 17.5099 12.4806 17.5447C13.6065 17.5796 14.7276 17.3836 15.7748 16.9687C16.8221 16.5538 17.7733 15.9288 18.5698 15.1323C19.3663 14.3358 19.9912 13.3847 20.4061 12.3374C20.821 11.2902 21.0171 10.1691 20.9822 9.04317C20.9473 7.91728 20.6822 6.81046 20.2033 5.79092C19.7243 4.77138 19.0417 3.86072 18.1974 3.11506Z"
                            fill="#CFCFD9"
                          />
                          <path
                            d="M5.5 16.5L6.1875 17.1875L7.61613 15.7589C7.36067 15.558 7.11732 15.3422 6.88737 15.1126L5.5 16.5Z"
                            fill="#637B97"
                          />
                          <path
                            d="M12.7188 2.40625C11.359 2.40625 10.0298 2.80946 8.89921 3.5649C7.76862 4.32033 6.88743 5.39406 6.36708 6.6503C5.84673 7.90654 5.71058 9.28888 5.97585 10.6225C6.24113 11.9561 6.89591 13.1811 7.85739 14.1426C8.81888 15.1041 10.0439 15.7589 11.3775 16.0241C12.7111 16.2894 14.0935 16.1533 15.3497 15.6329C16.6059 15.1126 17.6797 14.2314 18.4351 13.1008C19.1905 11.9702 19.5938 10.641 19.5938 9.28125C19.5917 7.45853 18.8667 5.71106 17.5778 4.4222C16.2889 3.13334 14.5415 2.40834 12.7188 2.40625ZM12.7188 15.4688C11.495 15.4688 10.2987 15.1059 9.28116 14.426C8.26363 13.7461 7.47057 12.7797 7.00225 11.6491C6.53393 10.5185 6.4114 9.27439 6.65014 8.07413C6.88889 6.87387 7.47819 5.77136 8.34353 4.90603C9.20887 4.04069 10.3114 3.45139 11.5116 3.21264C12.7119 2.9739 13.956 3.09643 15.0866 3.56475C16.2172 4.03306 17.1836 4.82613 17.8635 5.84366C18.5434 6.86119 18.9063 8.05748 18.9063 9.28125C18.9043 10.9217 18.2518 12.4944 17.0919 13.6544C15.9319 14.8143 14.3592 15.4668 12.7188 15.4688Z"
                            fill="#637B97"
                          />
                          <path
                            d="M12.7187 0.6875C10.4403 0.690048 8.25588 1.59628 6.64479 3.20737C5.0337 4.81845 4.12747 7.00283 4.12493 9.28125C4.11867 11.1066 4.70049 12.8855 5.78421 14.3543L0.788145 19.3507C0.723702 19.4152 0.6875 19.5026 0.6875 19.5938C0.6875 19.6849 0.723702 19.7723 0.788145 19.8368L2.16315 21.2118C2.22761 21.2762 2.31503 21.3124 2.40618 21.3124C2.49733 21.3124 2.58475 21.2762 2.64921 21.2118L7.64527 16.2157C9.11429 17.2994 10.8932 17.8812 12.7187 17.875C14.9979 17.875 17.1837 16.9696 18.7954 15.358C20.407 13.7463 21.3124 11.5605 21.3124 9.28125C21.3124 7.00205 20.407 4.81619 18.7954 3.20455C17.1837 1.59291 14.9979 0.6875 12.7187 0.6875ZM2.40618 20.4827L1.51724 19.5938L4.81243 16.2986L5.70136 17.1875L2.40618 20.4827ZM12.7187 17.1875C10.9429 17.1933 9.2179 16.5947 7.82746 15.4901C7.76133 15.438 7.6784 15.412 7.59439 15.417C7.51038 15.422 7.43113 15.4577 7.37165 15.5172L6.18743 16.7014L5.29849 15.8125L6.48271 14.6283C6.5422 14.5688 6.57778 14.4895 6.58273 14.4055C6.58767 14.3215 6.56162 14.2386 6.50952 14.1725C5.40506 12.7819 4.80657 11.057 4.81243 9.28125C4.81243 7.71754 5.27612 6.18895 6.14487 4.88877C7.01362 3.5886 8.24841 2.57523 9.69309 1.97683C11.1378 1.37842 12.7274 1.22185 14.2611 1.52692C15.7948 1.83198 17.2035 2.58498 18.3092 3.69069C19.4149 4.7964 20.1679 6.20516 20.473 7.73882C20.7781 9.27248 20.6215 10.8622 20.0231 12.3068C19.4247 13.7515 18.4113 14.9863 17.1112 15.8551C15.811 16.7238 14.2824 17.1875 12.7187 17.1875Z"
                            fill="#637B97"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_472_80">
                            <rect width={22} height={22} fill="white" />
                          </clipPath>
                        </defs>
                      </svg>
                    </span>
                    Daily Analysis
                  </Link>
                </li>
                <li>
                  <Link to="/trading-video">
                    <span>
                      <svg
                        width={24}
                        height={25}
                        viewBox="0 0 24 25"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M1 7.02481L11.6399 11.6005L12.3601 10.0325L1.7202 5.45682L1 7.02481ZM12.8867 23.3995V10.8165H11.1133V23.3995H12.8867ZM12.3601 11.6005L23 7.02481L22.2798 5.45682L11.6399 10.0325L12.3601 11.6005Z"
                          fill="#637B97"
                        />
                        <path
                          d="M1.35938 18.3784V6.68732C1.35938 6.41609 1.52446 6.1703 1.78062 6.06014L11.7112 1.7895C11.8946 1.71063 12.1039 1.71063 12.2874 1.7895L22.2179 6.06014C22.4741 6.1703 22.6392 6.41609 22.6392 6.68732V18.3784C22.6392 18.6496 22.4741 18.8954 22.2179 19.0056L12.2874 23.2762C12.1039 23.355 11.8946 23.355 11.7112 23.2762L1.78062 19.0056C1.52446 18.8954 1.35938 18.6496 1.35938 18.3784Z"
                          stroke="#637B97"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M6.67969 3.95428L16.8984 8.34881C17.1545 8.45897 17.3196 8.70476 17.3196 8.97599V13.1056"
                          stroke="#637B97"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                    Technical Analysis Videos
                  </Link>
                </li>
                <li>
                  <Link to="/blog">
                    <span>
                      <svg
                        width={22}
                        height={22}
                        viewBox="0 0 22 22"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g clipPath="url(#clip0_427_394)">
                          <path
                            d="M18.0929 9.52699C18.0681 10.0305 17.6861 10.6523 17.0704 11.2498V19.1238C17.0697 19.3266 16.9898 19.4984 16.8549 19.6327C16.7193 19.7643 16.5433 19.8422 16.3365 19.8435H3.5044C3.29896 19.8422 3.12236 19.7643 2.98675 19.6327C2.85179 19.4984 2.77259 19.3266 2.77191 19.1238V6.54066C2.77259 6.33858 2.85179 6.16602 2.98675 6.03174C3.12236 5.90012 3.29896 5.82227 3.5044 5.82093H10.3196C10.8708 5.09717 11.5133 4.35596 12.2411 3.67653C12.2445 3.6725 12.2472 3.66846 12.2506 3.66446H3.5044C2.69873 3.66377 1.95821 3.98806 1.43115 4.50639C0.901435 5.02472 0.571091 5.74982 0.571778 6.5407V19.1238C0.571091 19.9147 0.901435 20.6412 1.43115 21.1582C1.95817 21.6764 2.69873 22 3.5044 22H16.3366C17.1436 22 17.8842 21.6764 18.4112 21.1581C18.9396 20.6411 19.2706 19.9146 19.2699 19.1238V8.74281C19.2699 8.74281 19.2645 8.73611 19.2558 8.72936C18.9221 9.0154 18.5334 9.28795 18.0929 9.52699Z"
                            fill="#637B97"
                          />
                          <path
                            d="M21.4009 0C21.4009 0 20.7268 1.0071 16.3239 2.49687C11.8343 4.01625 9.07087 10.197 9.07087 10.197C8.40554 11.4633 5.77568 16.7243 5.77568 16.7243C5.05127 18.0831 6.37857 18.8861 7.15738 17.4829C8.64986 14.7893 9.6133 12.328 11.8826 12.2582C15.1932 12.1561 17.4571 9.26514 16.7448 9.39941C15.8075 9.80762 13.741 9.43031 14.9582 9.24232C17.8801 9.00801 19.6808 6.81188 19.094 6.66286C18.058 7.06166 17.0919 6.68302 16.9026 6.56885C22.0595 5.94176 21.4009 0 21.4009 0Z"
                            fill="#637B97"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_427_394">
                            <rect width={22} height={22} fill="white" />
                          </clipPath>
                        </defs>
                      </svg>
                    </span>
                    Blogs
                  </Link>
                </li>
            </SidebarGroup>
            <SidebarGroup title="Account" icon="fa-user-circle">
                <li>
                  <Link to="#">
                    <span>
                      <svg
                        width={24}
                        height={24}
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M11.9992 15.0378C13.8218 15.0378 15.2992 13.5825 15.2992 11.7873C15.2992 9.99202 13.8218 8.53677 11.9992 8.53677C10.1766 8.53677 8.69922 9.99202 8.69922 11.7873C8.69922 13.5825 10.1766 15.0378 11.9992 15.0378Z"
                          stroke="#637B97"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M20.3846 10.0474L19.1772 7.17542L20.8 5.28493L18.6 3.11793L16.6912 4.72471L13.7136 3.51854L13.0288 0.950928H10.8791L10.184 3.55255L7.27485 4.76047L5.4 3.11793L3.2 5.28493L4.79871 7.22315L3.60975 10.1025L1 10.7024V12.8694L3.64122 13.5797L4.86733 16.4447L3.2 18.2869L5.4 20.4539L7.37028 18.8724L10.2367 20.0339L10.9 22.6209H13.1L13.765 20.0348L16.6806 18.8455C17.1666 19.1877 18.6 20.4539 18.6 20.4539L20.8 18.2869L19.1675 16.3902L20.3753 13.5174L22.9999 12.8447L23 10.7024L20.3846 10.0474Z"
                          stroke="#637B97"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                    Clear Data/Cache
                  </Link>
                </li>
                <li>
                  <Link to="#">
                    <span>
                      <svg
                        width={24}
                        height={24}
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M11.9992 15.0378C13.8218 15.0378 15.2992 13.5825 15.2992 11.7873C15.2992 9.99202 13.8218 8.53677 11.9992 8.53677C10.1766 8.53677 8.69922 9.99202 8.69922 11.7873C8.69922 13.5825 10.1766 15.0378 11.9992 15.0378Z"
                          stroke="#637B97"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M20.3846 10.0474L19.1772 7.17542L20.8 5.28493L18.6 3.11793L16.6912 4.72471L13.7136 3.51854L13.0288 0.950928H10.8791L10.184 3.55255L7.27485 4.76047L5.4 3.11793L3.2 5.28493L4.79871 7.22315L3.60975 10.1025L1 10.7024V12.8694L3.64122 13.5797L4.86733 16.4447L3.2 18.2869L5.4 20.4539L7.37028 18.8724L10.2367 20.0339L10.9 22.6209H13.1L13.765 20.0348L16.6806 18.8455C17.1666 19.1877 18.6 20.4539 18.6 20.4539L20.8 18.2869L19.1675 16.3902L20.3753 13.5174L22.9999 12.8447L23 10.7024L20.3846 10.0474Z"
                          stroke="#637B97"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                    Share OTW
                  </Link>
                </li>
            </SidebarGroup>
            <li className="sidenav-top-item">
              <Link to="/pricing">
                <span><i className="fa fa-tags sidenav-item-icon" aria-hidden="true" /></span>
                Pricing
              </Link>
            </li>
            <li className="sidenav-top-item">
              <Link
                to="javascript:void(0);"
                data-bs-toggle="modal"
                data-bs-target="#notificationModel"
              >
                <span><i className="fa fa-bell sidenav-item-icon" aria-hidden="true" /></span>
                Notifications
              </Link>
            </li>
            <li className="sidenav-top-item">
              <Link
                to="javascript:void(0);"
                data-bs-toggle="modal"
                data-bs-target="#settingModel"
              >
                <span><i className="fa fa-cog sidenav-item-icon" aria-hidden="true" /></span>
                Settings
              </Link>
            </li>
            <SidebarGroup title="About &amp; Support" icon="fa-info-circle">
                <li>
                  <Link to="/about">
                    <span>
                      <svg
                        width={24}
                        height={25}
                        viewBox="0 0 24 25"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M1 7.02481L11.6399 11.6005L12.3601 10.0325L1.7202 5.45682L1 7.02481ZM12.8867 23.3995V10.8165H11.1133V23.3995H12.8867ZM12.3601 11.6005L23 7.02481L22.2798 5.45682L11.6399 10.0325L12.3601 11.6005Z"
                          fill="#637B97"
                        />
                        <path
                          d="M1.35938 18.3784V6.68732C1.35938 6.41609 1.52446 6.1703 1.78062 6.06014L11.7112 1.7895C11.8946 1.71063 12.1039 1.71063 12.2874 1.7895L22.2179 6.06014C22.4741 6.1703 22.6392 6.41609 22.6392 6.68732V18.3784C22.6392 18.6496 22.4741 18.8954 22.2179 19.0056L12.2874 23.2762C12.1039 23.355 11.8946 23.355 11.7112 23.2762L1.78062 19.0056C1.52446 18.8954 1.35938 18.6496 1.35938 18.3784Z"
                          stroke="#637B97"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M6.67969 3.95428L16.8984 8.34881C17.1545 8.45897 17.3196 8.70476 17.3196 8.97599V13.1056"
                          stroke="#637B97"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                    About Us
                  </Link>
                </li>
                <li>
                  <Link to="/contact">
                    <span>
                      <svg
                        width={24}
                        height={24}
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M18.6878 15.4495L14.1129 16.3227C11.0228 14.7949 9.11394 13.04 8.00305 10.3045L8.85838 5.78523L7.24156 1.5507H3.07472C1.82214 1.5507 0.835777 2.57027 1.02285 3.79022C1.48988 6.83579 2.86691 12.3578 6.89217 16.3227C11.1193 20.4864 17.2075 22.2932 20.5583 23.0113C21.8522 23.2887 23 22.2944 23 20.9901V17.0681L18.6878 15.4495Z"
                          stroke="#637B97"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                    Contact Us
                  </Link>
                </li>
            </SidebarGroup>
            <SidebarGroup title="Policies" icon="fa-file-text-o">
                <li>
                  <Link to="/privacy-policy">
                    <span>
                      <svg
                        width={24}
                        height={26}
                        viewBox="0 0 24 26"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M5.88867 19.8591H10.1664H14.4442"
                          stroke="#637B97"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M5.88867 15.1312H6.49978H7.11089"
                          stroke="#637B97"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M5.88867 10.4034H7.72201H9.55534"
                          stroke="#637B97"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M5.88892 0.947282H17.5L23 6.26644V21.0419"
                          stroke="#637B97"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M1 22.8143V6.26583C1 5.2866 1.82081 4.49278 2.83333 4.49278H14.7518C14.9463 4.49278 15.1328 4.5675 15.2703 4.70051L19.1186 8.42217C19.2561 8.55519 19.3333 8.73558 19.3333 8.92368V22.8143C19.3333 23.7935 18.5125 24.5874 17.5 24.5874H2.83333C1.82081 24.5874 1 23.7935 1 22.8143Z"
                          stroke="#637B97"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M14.4453 4.49278V8.5117C14.4453 8.90339 14.7736 9.22092 15.1786 9.22092H19.3342"
                          stroke="#637B97"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/term-and-services">
                    <span>
                      <svg
                        width={24}
                        height={26}
                        viewBox="0 0 24 26"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M5.88867 19.8591H10.1664H14.4442"
                          stroke="#637B97"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M5.88867 15.1312H6.49978H7.11089"
                          stroke="#637B97"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M5.88867 10.4034H7.72201H9.55534"
                          stroke="#637B97"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M5.88892 0.947282H17.5L23 6.26644V21.0419"
                          stroke="#637B97"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M1 22.8143V6.26583C1 5.2866 1.82081 4.49278 2.83333 4.49278H14.7518C14.9463 4.49278 15.1328 4.5675 15.2703 4.70051L19.1186 8.42217C19.2561 8.55519 19.3333 8.73558 19.3333 8.92368V22.8143C19.3333 23.7935 18.5125 24.5874 17.5 24.5874H2.83333C1.82081 24.5874 1 23.7935 1 22.8143Z"
                          stroke="#637B97"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M14.4453 4.49278V8.5117C14.4453 8.90339 14.7736 9.22092 15.1786 9.22092H19.3342"
                          stroke="#637B97"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                    Terms of Services
                  </Link>
                </li>
                <li>
                  <Link
                    to="javascript:void(0);"
                    data-bs-toggle="modal"
                    data-bs-target="#disclaimerModel"
                  >
                    <span>
                      <svg
                        width={24}
                        height={24}
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M11.9999 6.47865V9.75871M12.0199 2.10524C8.3399 2.10524 5.3599 5.04055 5.3599 8.66535V10.7339C5.3599 11.4037 5.0799 12.4084 4.7299 12.9797L3.4599 15.0679C2.6799 16.3582 3.2199 17.7963 4.6599 18.2691C9.44118 19.8353 14.6086 19.8353 19.3899 18.2691C19.7054 18.1655 19.9932 17.9933 20.2321 17.7654C20.471 17.5374 20.6548 17.2595 20.77 16.9522C20.8852 16.6449 20.9288 16.316 20.8976 15.9898C20.8665 15.6637 20.7613 15.3486 20.5899 15.0679L19.3199 12.9797C18.9699 12.4084 18.6899 11.3938 18.6899 10.7339V8.66535C18.6799 5.06025 15.6799 2.10524 12.0199 2.10524Z"
                          stroke="#637B97"
                          strokeWidth="1.5"
                          strokeMiterlimit={10}
                          strokeLinecap="round"
                        />
                        <path
                          d="M15.3299 18.673C15.3299 20.4755 13.8299 21.953 11.9999 21.953C11.0899 21.953 10.2499 21.5787 9.64992 20.9877C9.04992 20.3967 8.66992 19.5693 8.66992 18.673"
                          stroke="#637B97"
                          strokeWidth="1.5"
                          strokeMiterlimit={10}
                        />
                      </svg>
                    </span>
                    Disclaimer
                  </Link>
                </li>
                <li>
                  <Link to="/cancelpolicy">
                    <span>
                      <svg
                        width={24}
                        height={24}
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M11.9992 15.0378C13.8218 15.0378 15.2992 13.5825 15.2992 11.7873C15.2992 9.99202 13.8218 8.53677 11.9992 8.53677C10.1766 8.53677 8.69922 9.99202 8.69922 11.7873C8.69922 13.5825 10.1766 15.0378 11.9992 15.0378Z"
                          stroke="#637B97"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M20.3846 10.0474L19.1772 7.17542L20.8 5.28493L18.6 3.11793L16.6912 4.72471L13.7136 3.51854L13.0288 0.950928H10.8791L10.184 3.55255L7.27485 4.76047L5.4 3.11793L3.2 5.28493L4.79871 7.22315L3.60975 10.1025L1 10.7024V12.8694L3.64122 13.5797L4.86733 16.4447L3.2 18.2869L5.4 20.4539L7.37028 18.8724L10.2367 20.0339L10.9 22.6209H13.1L13.765 20.0348L16.6806 18.8455C17.1666 19.1877 18.6 20.4539 18.6 20.4539L20.8 18.2869L19.1675 16.3902L20.3753 13.5174L22.9999 12.8447L23 10.7024L20.3846 10.0474Z"
                          stroke="#637B97"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                    Cancellation &amp; Refund Policy
                  </Link>
                </li>
            </SidebarGroup>
          </ul>
        </div>
        <div className="user-profile-section">
          {isLoggedIn ? (
            <Link
              to="#"
              onClick={(e) => {
                e.preventDefault(); // Prevent default anchor behavior
                handleProtectedNavigation("/profile");
              }}
            >
              <div className="user-img">
                <img src="img/user-img.svg" alt="" />
              </div>
              <div className="user-name">
                <p>{displayUserName(localStorage.getItem("user"))}</p>
                <small>View profile</small>
              </div>
              <i className="fa fa-chevron-right user-go" aria-hidden="true" />
            </Link>
          ) : (
            <Link to="#" onClick={() => handleProtectedNavigation("#")}>
              <div className="user-name">
                <p>Login</p>
              </div>
            </Link>
          )}
        </div>
      </div>

      <div className="modal fade" id="searchNiftyModel" tabIndex="-1" aria-labelledby="searchNiftyModelLabel" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content modal-bg-light-green">
            <div className="modal-body py-4 px-4">
              <form className="search-field-container">
                <div className="form-input">
                  <input
                    type="search"
                    name="Search Nifty"
                    className="form-control"
                    placeholder="Search Stock/Index"
                    value={searchQuery}
                    onChange={handleSearchChange}
                  />
                </div>
              </form>
              <div className="nifty-index-list">
                <ul className="list-unstyled">
                  {filteredStock.length > 0 ? (
                    [...filteredStock]
                      .sort((a, b) => {
                        const aFav = favoriteList.includes(a.name);
                        const bFav = favoriteList.includes(b.name);
                        return aFav === bFav ? 0 : aFav ? -1 : 1;
                      })
                      .map((item, index) => {
                        const isFavorite = favoriteList.includes(item.name);
                        return (
                          <li key={index} className="d-flex justify-content-between ">
                            <button
                              className="btn btn-link text-start flex-grow-1"
                              style={{borderRadius:0}}
                              onClick={() => handleSetLocalStorage(item.name, item.lotSize, item)}
                            >
                              <h6>{item.name}</h6>
                              <p className="mb-0">{item.company}</p>
                            </button>
                            <button
                              className="btn btn-sm"
                              style={{borderRadius:0}}
                              onClick={() => toggleFavorite(item.name)}
                            >
                              <span style={{ color: isFavorite ? 'gold' : 'gray', fontSize: '1.5rem' }}>
                                {isFavorite ? '★' : '☆'}
                              </span>
                            </button>
                          </li>
                        );
                      })
                  ) : (
                    <li>No results found</li>
                  )}
                </ul>

              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal fade"
        id="settingModel"
        tabIndex={-1}
        aria-labelledby="settingModelLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content modal-bg-light-blue">
            <div className="modal-header">
              <h5 className="modal-title" id="settingModelLabel">
                Setting
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <div className="modal-body">
              <div className="setting-functionalty-outer popup-list">
                <ul className="list-unstyled">
                  <li>
                    <div className="popup-list-title">
                      <span>
                        <img src="img/light-mode.svg" alt="icon" />
                      </span>
                      <span>Dark Mode</span>
                    </div>
                    <div className="toggle-status">
                      <div className="form-check form-switch">
                        {/* Same theme as the header's sun / moon button: on = dark, off = light */}
                        <input
                          className="form-check-input light-mode"
                          type="checkbox"
                          aria-label="Dark mode"
                          checked={theme === "dark"}
                          onChange={(e) => setTheme(e.target.checked ? "dark" : "light")}
                        />
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className="popup-list-title">
                      <span>
                        <img src="img/highlight.svg" alt="icon" />
                      </span>
                      <span>ITM & OTM Highlight</span>
                    </div>
                    <div className="toggle-status">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input itm-otm-highlight"
                          type="checkbox"
                          checked={itmOtmHighlight}
                          onChange={() =>
                            handleToggle("itmOtmHighlight", setItmOtmHighlight)
                          }
                        />
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className="popup-list-title">
                      <span>
                        <img src="img/screen-desktop.svg" alt="icon" />
                      </span>
                      <span>Keep Screen Awake</span>
                    </div>
                    <div className="toggle-status">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input keep-screen-awake"
                          type="checkbox"
                          checked={keepScreenAwake}
                          onChange={() =>
                            handleToggle("keepScreenAwake", setKeepScreenAwake)
                          }
                        />
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className="popup-list-title">
                      <span>
                        <img src="img/price-tag.svg" alt="icon" />
                      </span>
                      <span>Show Future Price</span>
                    </div>
                    <div className="toggle-status">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input show-future-price"
                          type="checkbox"
                          checked={showFuturePrice}
                          onChange={() =>
                            handleToggle("showFuturePrice", setShowFuturePrice)
                          }
                        />
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className="popup-list-title">
                      <span>
                        <img src="img/top-so.svg" alt="icon" />
                      </span>
                      <span>High Third Highest</span>
                    </div>
                    <div className="toggle-status">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input high-third-highest"
                          type="checkbox"
                          checked={highThirdHighest}
                          onChange={() =>
                            handleToggle(
                              "highThirdHighest",
                              setHighThirdHighest
                            )
                          }
                        />
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className="popup-list-title">
                      <span>
                        <img src="img/number-sign.svg" alt="icon" />
                      </span>
                      <span>Show Numbers</span>
                    </div>
                    <div className="toggle-status">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input show-numbers"
                          type="checkbox"
                          checked={showNumbers}
                          onChange={() =>
                            handleToggle("showNumbers", setShowNumbers)
                          }
                        />
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className="popup-list-title">
                      <span>
                        <img src="img/table-column.svg" alt="icon" />
                      </span>
                      <span>Reverse Table</span>
                    </div>
                    <div className="toggle-status">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input reverse-table"
                          type="checkbox"
                          checked={reverseTable}
                          onChange={() =>
                            handleToggle("reverseTable", setReverseTable)
                          }
                        />
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className="popup-list-title">
                      <span>
                        <img src="img/percentage.svg" alt="icon" />
                      </span>
                      <span>Prefer Percentage</span>
                    </div>
                    <div className="toggle-status">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input prefer-percentage"
                          type="checkbox"
                          checked={preferPercentage}
                          onChange={() =>
                            handleToggle(
                              "preferPercentage",
                              setPreferPercentage
                            )
                          }
                        />
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className="popup-list-title">
                      <span>
                        <img src="img/show-qt.svg" alt="icon" />
                      </span>
                      <span>Show QT in Lots</span>
                    </div>
                    <div className="toggle-status">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input show-qt"
                          type="checkbox"
                          checked={showQt}
                          onChange={() => handleToggle("showQt", setShowQt)}
                        />
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className="popup-list-title">
                      <span>
                        <img src="img/trash.svg" alt="icon" />
                      </span>
                      <span>Clear Data Cache</span>
                    </div>
                    <div className="toggle-status">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input clear-data-cache"
                          type="checkbox"
                          checked={clearDataCache}
                          onChange={() =>
                            handleToggle("clearDataCache", setClearDataCache)
                          }
                        />
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className="popup-list-title">
                      <span>
                        <img src="img/trash.svg" alt="icon" />
                      </span>
                      <span>Coloum Show</span>
                    </div>
                    <div className="toggle-status">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input clear-data-cache"
                          type="checkbox"
                          checked={showColoum}
                          onChange={() =>
                            handleToggle("showColoum", setshowColoum)
                          }
                        />
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className="popup-list-title">
                      <span>
                        <img src="img/show-qt.svg" alt="icon" />
                      </span>
                      <div>
                        <span>Show Data in Lots</span>
                        <p className="mb-0" style={{ fontSize: '0.75rem', color: '#aaa' }}>Display values in lot format</p>
                      </div>
                    </div>
                    <div className="toggle-status">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input show-data-in-lots"
                          type="checkbox"
                          checked={showDataInLots}
                          onChange={() =>
                            handleToggle("showDataInLots", setShowDataInLots)
                          }
                        />
                      </div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal fade"
        id="notificationModel"
        tabIndex={-1}
        aria-labelledby="notificationModelLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content modal-bg-light-blue">
            <div className="modal-header">
              <h5 className="modal-title" id="notificationModelLabel">
                Notification Setting
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <div className="modal-body">
              <div className="notification-functionalty-outer popup-list">
                <ul className="list-unstyled">
                  <li>
                    <div className="popup-list-title">
                      <span>
                        <img src="img/stock-ticker.svg" alt="icon" />
                      </span>
                      <span>Stocks</span>
                    </div>
                    <div className="toggle-status">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input stocks"
                          type="checkbox"
                          id="flexSwitchCheckChecked"
                          defaultChecked
                        />
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className="popup-list-title">
                      <span>
                        <img src="img/data-collection.svg" alt="icon" />
                      </span>
                      <span>Indices</span>
                    </div>
                    <div className="toggle-status">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input indices"
                          type="checkbox"
                          id="flexSwitchCheckChecked"
                          defaultChecked
                        />
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className="popup-list-title">
                      <span>
                        <img src="img/currency.svg" alt="icon" />
                      </span>
                      <span>Currency</span>
                    </div>
                    <div className="toggle-status">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input currency"
                          type="checkbox"
                          id="flexSwitchCheckChecked"
                          defaultChecked
                        />
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className="popup-list-title">
                      <span>
                        <img src="img/stock_mcx.svg" alt="icon" />
                      </span>
                      <span>MCX</span>
                    </div>
                    <div className="toggle-status">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input mcx"
                          type="checkbox"
                          id="flexSwitchCheckChecked"
                          defaultChecked
                        />
                      </div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* login popup */}
      <div
        className="modal fade"
        id="loginModel"
        tabIndex={-1}
        aria-labelledby="loginModelLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content modal-bg-light-blue">
            <div className="modal-body py-4 px-5">
              <div className="header-logo text-center">
                <Link to="#">
                  <img src="img/header-logo.svg" alt="logo" />
                </Link>
              </div>
              <div className="login-title text-center my-3">
                <h4>Login</h4>
                <h5>
                  Welcome to Option Trading Wala <br /> Please Login to Continue
                </h5>
              </div>
              <div className="login-form-outer">
                <div className="form-input mb-3">
                  <label className="form-label">Email / Phone No</label>
                  <input
                    type="text"
                    name="email"
                    onChange={(e) => setEmail(e.target.value)}
                    className="form-control"
                    placeholder="Enter Email or Phone No."
                  />
                </div>
                <div className="form-input mb-3">
                  <label className="form-label">Password</label>
                  <input
                    type="password"
                    name="password"
                    onChange={(e) => setPaasword(e.target.value)}
                    className="form-control"
                    placeholder="Enter Password"
                  />
                </div>

                {otpError && <p className="text-danger">{otpError}</p>}
                {successMsg && <p className="text-success">{successMsg}</p>}

                {showOtpInput && (
                  <div className="otp-outer text-center">
                    <p>Enter your OTP</p>
                    <div className="otp-code-inner">
                      <input
                        type="text"
                        maxLength={6}
                        value={otpValue}
                        onChange={(e) => setOtpValue(e.target.value)}
                        className="form-control text-center"
                        style={{ width: "150px", margin: "0 auto" }}
                      />
                    </div>
                    <div className="d-grid mt-3">
                      <button className="btn btn-success" onClick={handleOTPVerification}>
                        Verify OTP
                      </button>
                    </div>
                    <p className="mt-2">
                      <button
                        className="btn btn-link text-danger"
                        onClick={(e) => handleLogin(e)}
                      >
                        Resend OTP
                      </button>
                    </p>
                  </div>
                )}

                {!showOtpInput && (
                  <div className="d-grid">
                    <button onClick={handleLogin} className="btn btn-light-green">
                      Send OTP
                    </button>
                  </div>
                )}

                <div className="d-grid mt-3">
                  <button
                    className="btn btn-light-gray"
                    data-bs-toggle="modal"
                    data-bs-target="#signupModel"
                  >
                    Signup
                  </button>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
      {/* signup popup */}
      <div
        className="modal fade"
        id="signupModel"
        tabIndex={-1}
        aria-labelledby="signupModelLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content modal-bg-light-blue">
            <div className="modal-body py-4 px-5">
              <div className="header-logo text-center">
                <Link to="#">
                  <img src="img/header-logo.svg" alt="logo" />
                </Link>
              </div>
              <div className="login-title text-center my-3">
                <h4>Register</h4>
                <h5>
                  Welcome to Option Trading Wala <br /> Please Register to
                  Continue
                </h5>
              </div>
              <div className="login-form-outer">
                <div className="form-input mb-3">
                  <label
                    htmlFor="exampleFormControlInput1"
                    className="form-label"
                  >
                    Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    className="form-control"
                    placeholder="Enter Name"
                    onChange={handleSignupInputChange}
                  />
                </div>
                <div className="form-input mb-3">
                  <label
                    htmlFor="exampleFormControlInput2"
                    className="form-label"
                  >
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    className="form-control"
                    placeholder="Enter Email"
                    onChange={handleSignupInputChange}
                  />
                </div>
                <div className="form-input mb-3">
                  <label
                    htmlFor="exampleFormControlInput3"
                    className="form-label"
                  >
                    Phone No.
                  </label>
                  <input
                    type="number"
                    maxLength={10}
                    name="mobileNo"
                    className="form-control"
                    placeholder="Enter Phone No."
                    onChange={handleSignupInputChange}
                  />
                </div>
                <div className="form-input mb-3">
                  <label
                    htmlFor="exampleFormControlInput4"
                    className="form-label"
                  >
                    Password
                  </label>
                  <input
                    type="password"
                    name="password"
                    className="form-control"
                    placeholder="Enter Password"
                    onChange={handleSignupInputChange}
                  />
                </div>
                {showOtpInput && (
                  <div
                    className="otp-outer text-center"
                    style={{ display: "block" }}
                  >
                    <p>Enter your OTP</p>
                    <div className="otp-code-inner">
                      <input
                        type="number"
                        name="otp"
                        maxLength={6}
                        onChange={(e) => setOtpValue(e.target.value)} // Store the OTP value in a state
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            handleSignUpOTPVerification(e.target.value);
                          }
                        }}
                      />
                    </div>
                    <p>
                      <Link
                        to="#"
                        onClick={handleSignup}
                        className="text-danger"
                      >
                        Resend OTP In
                      </Link>
                      <span className="otp-time ms-2">00:28</span>
                    </p>
                  </div>
                )}

                <button onClick={handleSignup} className="submit-btn">
                  Sign Up
                </button>

                <div className="signup-and-forgot-pswd d-flex justify-content-center mt-2">
                  <p>
                    Already have an Account?{" "}
                    <Link to="#" className="text-danger">
                      Login
                    </Link>
                  </p>
                </div>
                <div className="privacy-policy-and-terms-&-service text-center mt-4">
                  <p>
                    By Clicking on login you agree to <br />
                    <Link to="#">Privacy Policy</Link> &amp;{" "}
                    <Link to="#">Terms &amp; Service</Link>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ======== Disclaimer popup ======== */}
      <div
        className="modal fade"
        id="disclaimerModel"
        tabIndex={-1}
        aria-labelledby="disclaimerModelLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content modal-bg-light-blue">
            <div className="modal-header">
              <h5 className="modal-title" id="disclaimerModelLabel">
                Disclaimer
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <div className="modal-body">
              <div className="disclaimer-functionalty-outer">
                {/* Disclaimer text (fixed in the frontend) */}
                <div className="otw-disclaimer">
                  <p>
                    Option Trading Wala (OTW) provides option-chain analytics, support / resistance
                    levels, stock screeners, charts and a historical backtester for
                    <strong> information and educational purposes only</strong>.
                  </p>
                  <h6>Not investment advice</h6>
                  <p>
                    Nothing on this platform is a recommendation or an offer to buy or sell any
                    security, derivative or commodity. Levels, signals and labels (such as support,
                    resistance, bullish or bearish) are generated from market data using fixed rules
                    and are not personalised advice. Please consult a qualified, registered financial
                    adviser before making investment decisions.
                  </p>
                  <h6>Market and trading risk</h6>
                  <ul>
                    <li>Trading in equities, futures, options and commodities involves substantial risk and may result in losses greater than your initial investment.</li>
                    <li>Options and leveraged products can lose value quickly, especially close to expiry.</li>
                    <li>Past performance, backtest results and historical patterns do not guarantee future returns.</li>
                  </ul>
                  <h6>Data accuracy</h6>
                  <p>
                    Market data comes from third-party data providers and may be delayed, incomplete
                    or contain errors. Always verify prices and positions with your broker before
                    placing any trade.
                  </p>
                  <p className="mb-0">
                    You are solely responsible for your trading decisions and their outcomes. By
                    clicking <strong>Accept &amp; Continue</strong> you confirm that you have read and
                    understood this disclaimer.
                  </p>
                </div>
                <div className="modal-footer mt-3">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    data-bs-dismiss="modal"
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary site-btn"
                    data-bs-dismiss="modal"
                  >
                    Accept &amp; Continue
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Header;
